import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

const SEO = ({
  title,
  description,
  keywords,
  image = 'https://cetoex.com/assets/og-image.jpg',
  type = 'website',
  noindex = false,
  structuredData = null,
  alternateLanguages = null
}) => {
  const location = useLocation();
  const currentUrl = `https://cetoex.com${location.pathname}`;
  
  // Get page-specific SEO data based on route
  const getPageSEO = () => {
    const path = location.pathname;
    
    const seoData = {
      '/': {
        title: 'CetoEX Exchange - Buy & Sell Bitcoin, Ethereum & 100+ Cryptocurrencies',
        description: 'Trade cryptocurrencies with the lowest fees globally. Buy & sell Bitcoin, Ethereum, and 100+ altcoins on CetoEX - the fastest and most secure crypto exchange.',
        keywords: 'cryptocurrency exchange, buy bitcoin, sell ethereum, crypto trading, USDT trading, lowest fees, secure crypto exchange, CetoEX'
      },
      '/markets': {
        title: 'Crypto Markets - Live Cryptocurrency Prices | CetoEX Exchange',
        description: 'View live cryptocurrency prices, market cap, and trading volumes. Track Bitcoin, Ethereum, and 100+ crypto markets in real-time on CetoEX.',
        keywords: 'crypto prices, cryptocurrency market, bitcoin price, ethereum price, live crypto rates, market cap, trading volume'
      },
      '/trade': {
        title: 'Trade Cryptocurrencies - Advanced Trading Platform | CetoEX',
        description: 'Professional cryptocurrency trading platform with advanced charts, order types, and lowest trading fees. Trade Bitcoin, Ethereum and altcoins 24/7.',
        keywords: 'crypto trading platform, bitcoin trading, ethereum trading, advanced charts, trading tools, order book'
      },
      '/wallet': {
        title: 'Secure Crypto Wallet - Store Your Digital Assets | CetoEX',
        description: 'Secure cryptocurrency wallet with multi-layer security. Store, send, and receive Bitcoin, Ethereum, and 100+ cryptocurrencies safely.',
        keywords: 'crypto wallet, secure wallet, bitcoin wallet, ethereum wallet, digital asset storage, cryptocurrency security'
      },
      '/launchpad': {
        title: 'Crypto Launchpad - Early Access to New Projects | CetoEX',
        description: 'Get early access to promising cryptocurrency projects on CetoEX Launchpad. Participate in token sales and discover the next big crypto gems.',
        keywords: 'crypto launchpad, token sale, new crypto projects, early access, cryptocurrency investment, token listing'
      },
      '/staking': {
        title: 'Crypto Staking - Earn Rewards on Your Holdings | CetoEX',
        description: 'Stake your cryptocurrencies and earn passive income. High APY staking rewards for Ethereum, Cardano, and other proof-of-stake tokens.',
        keywords: 'crypto staking, earn crypto rewards, passive income, staking rewards, ETH staking, ADA staking, high APY'
      },
      '/airdrop': {
        title: 'Free Crypto Airdrops - Claim Free Tokens | CetoEX',
        description: 'Claim free cryptocurrency airdrops and bonus tokens. Get free crypto by participating in CetoEX airdrop campaigns and promotions.',
        keywords: 'crypto airdrops, free tokens, free cryptocurrency, bonus crypto, airdrop campaigns, claim free coins'
      },
      '/about-cetoex': {
        title: 'About CetoEX - Leading Cryptocurrency Exchange Platform',
        description: 'Learn about CetoEX, the trusted cryptocurrency exchange with millions of users worldwide. Our mission, security features, and commitment to crypto innovation.',
        keywords: 'about CetoEX, cryptocurrency exchange, crypto platform, company information, mission, security, innovation'
      },
      '/contact': {
        title: 'Contact CetoEX - Get in Touch with Our Team | Crypto Exchange',
        description: 'Contact CetoEX team for listing inquiries, partnerships, and support. Connect with our Listing Head, Manager, and Developer through Telegram and social media.',
        keywords: 'CetoEX contact, crypto listing, partnership, support, telegram, social media, listing head, listing manager, Abdul Rahman, Crishbanner, CetoexSupport'
      },

      '/fee-schedule': {
        title: 'Trading Fees - Transparent Fee Structure | CetoEX Exchange',
        description: 'View CetoEX trading fees and commission structure. Enjoy some of the lowest cryptocurrency trading fees in the industry with transparent pricing.',
        keywords: 'trading fees, crypto fees, commission structure, lowest fees, transparent pricing, fee schedule'
      },
      '/referral-program': {
        title: 'Referral Program - Earn Commission on CetoEX',
        description: 'Earn lifetime commissions by referring friends to CetoEX. Join our referral program and get paid for every trade your referrals make.',
        keywords: 'referral program, earn commission, refer friends, crypto affiliate, lifetime commission, earn crypto'
      },
      '/coin-listing': {
        title: 'List Your Token - Cryptocurrency Listing on CetoEX',
        description: 'List your cryptocurrency token on CetoEX exchange. Fast listing process, global exposure, and access to millions of traders worldwide.',
        keywords: 'token listing, list cryptocurrency, coin listing, exchange listing, crypto project listing, token submission'
      },
      '/faq': {
        title: 'FAQ - Frequently Asked Questions | CetoEX Exchange',
        description: 'Find answers to common questions about cryptocurrency trading, account setup, security, deposits, withdrawals, and more on CetoEX.',
        keywords: 'FAQ, help, questions, crypto trading help, account help, security questions, deposit withdrawal help'
      },
      '/privacy': {
        title: 'Privacy Policy - Data Protection & Privacy | CetoEX',
        description: 'CetoEX privacy policy and data protection practices. Learn how we protect your personal information and maintain your privacy.',
        keywords: 'privacy policy, data protection, personal information, privacy practices, user privacy, data security'
      }
    };

    // Handle dynamic routes
    if (path.startsWith('/trade/')) {
      const pair = path.split('/')[2] || 'BTCUSDT';
      return {
        title: `Trade ${pair} - Live ${pair} Price & Charts | CetoEX`,
        description: `Trade ${pair} with real-time charts and advanced tools. Get the best ${pair} price and lowest trading fees on CetoEX exchange.`,
        keywords: `${pair} trading, ${pair} price, ${pair} chart, cryptocurrency trading, live crypto prices`
      };
    }

    if (path.startsWith('/create-account')) {
      return {
        title: 'Create Account - Join CetoEX Cryptocurrency Exchange',
        description: 'Create your free CetoEX account and start trading cryptocurrencies today. Fast verification, secure platform, and lowest trading fees.',
        keywords: 'create account, sign up, register, join CetoEX, crypto account, cryptocurrency trading account'
      };
    }

    return seoData[path] || seoData['/'];
  };

  const pageSEO = getPageSEO();
  const finalTitle = title || pageSEO.title;
  const finalDescription = description || pageSEO.description;
  const finalKeywords = keywords || pageSEO.keywords;

  return (
    <Helmet>
      {/* Basic SEO */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="CetoEX Exchange" />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow'} />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="1 days" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalTitle} />
      <meta property="og:site_name" content="CetoEX Exchange" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={finalTitle} />
      <meta name="twitter:creator" content="@CetoEX" />
      <meta name="twitter:site" content="@CetoEX" />
      
      {/* Telegram */}
      <meta property="telegram:channel" content="@cetoex_official" />
      <meta property="telegram:title" content={finalTitle} />
      <meta property="telegram:description" content={finalDescription} />
      <meta property="telegram:image" content={image} />
      
      {/* WhatsApp */}
      <meta property="whatsapp:title" content={finalTitle} />
      <meta property="whatsapp:description" content={finalDescription} />
      <meta property="whatsapp:image" content={image} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Alternate Languages */}
      {alternateLanguages && alternateLanguages.map((lang, index) => (
        <link key={index} rel="alternate" hrefLang={lang.hreflang} href={lang.href} />
      ))}
      
      {/* Favicon for all pages */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="shortcut icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon.png" />
      <link rel="icon" type="image/png" sizes="48x48" href="/favicon.png" />
      <link rel="icon" type="image/png" sizes="192x192" href="/logo192.png" />
      <link rel="apple-touch-icon" href="/favicon.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicon.png" />
      
      {/* Additional meta tags for crypto/finance */}
      <meta name="theme-color" content="#1a73e8" />
      <meta name="msapplication-TileColor" content="#1a73e8" />
      <meta name="msapplication-TileImage" content="/favicon.png" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="CetoEX" />
      
      {/* Security headers */}
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta httpEquiv="Content-Security-Policy" content="upgrade-insecure-requests" />
      
      {/* Performance and DNS */}
      <link rel="dns-prefetch" href="//cetoex.com" />
      <link rel="dns-prefetch" href="//api.cetoex.com" />
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="preconnect" href="https://cetoex.com" crossOrigin="anonymous" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Default structured data for organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "CetoEX Exchange",
          "url": "https://cetoex.com",
          "logo": "https://cetoex.com/logo.png",
          "description": "Leading cryptocurrency exchange platform for buying, selling, and trading digital assets",
          "sameAs": [
            "https://twitter.com/CetoEX",
            "https://t.me/cetoex_official",
            "https://linkedin.com/company/cetoex"
          ],

        })}
      </script>
    </Helmet>
  );
};

export default SEO; 